/** @jsxImportSource @emotion/react */
import { css, CSSObject } from "@emotion/react";

import { SetStateAction, useRef, useState } from "react";

import moment from "moment";
import { pushDataLayerEvent } from "@/shared/hooks/useGoogleHooks/analytics";
import useLanguageConverter from "@/shared/hooks/useLanguageConverter";
import { useUserDataStore } from "@/stores/useUserDataStore";
import Card from "@mui/material/Card";

import { NewsDto } from "../..";
import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import { MenuAnchorEl, NEWS_ACTIONS } from "../../../../../../shared";
import { imageList } from "../../config";
import DeleteNewsDialog from "../../Dialogs/DeleteNewsDialog/DeleteNewsDialog";
import EditNewsDialog from "../../Dialogs/EditNewsDialog/EditNewsDialog";
import PublishNewsDialog from "../../Dialogs/PublishNewsDialog/PublishNewsDialog";
import ReleaseNotesDialog from "../../Dialogs/ReleaseNotesDialog/ReleaseNotesDialog";
import SingleNewsDialog from "../../Dialogs/SingleNewsDialog/SingleNewsDialog";
import useSingleNew from "../../hooks/useSingleNews";
import { useStyles } from "../../styles";
import { NEWS_SECTION } from "../../types/enums";
import DraftAdditions from "../DraftAdditions";
import ListedNewsItemContent from "../ListedNewsItemContent";
import NewsImageContainer from "../NewsImageContainer";

interface SingleNewsProps {
  newsData: NewsDto;
  isReleaseNotes: boolean;
  noMargin?: any;
}

const SingleNews = ({
  noMargin,
  newsData,
  isReleaseNotes = false
}: SingleNewsProps) => {
  const userData = useUserDataStore(state => state.userData);
  const { convertToLocale } = useLanguageConverter();
  const { cookieConsentStatus } = useAuth();

  const [isActionsOpen, setIsActionsOpen] = useState<MenuAnchorEl>(null);

  const [isSingleNewsDialogOpen, setIsSingleNewsDialogOpen] =
    useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isPublishDialogOpen, setIsPublishDialogOpen] =
    useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isReleaseNotesDialogOpen, setIsReleaseNotesDialogOpen] =
    useState<boolean>(false);
  const [fetchedSingleNews, setFetchedSingleNews] = useState<NewsDto | null>(
    null
  );

  const newsOpenedAtRef = useRef<number | null>(null);
  const releaseNotesOpenedAtRef = useRef<number | null>(null);

  const { getSingleNews, isLoading, handleCloseActionsMenu } = useSingleNew({
    id: newsData.id,
    setIsActionsOpen
  });

  const { textColumn, newsCard, cardNoMargin } = useStyles();

  const unpublishedNew = newsData.draft;

  const imageFromResponse =
    newsData?.imageName?.length > 0 && newsData.imageName;

  const releaseNotesImage = isReleaseNotes && `${imageList[1].imageName}`;

  const userInfo = userData
    ? {
        user_id: userData.id,
        user_company_id: userData.company.id,
        user_language: convertToLocale(userData.language!)
      }
    : {};

  const getElapsedSeconds = (openedAtRef: React.RefObject<number | null>) => {
    if (!openedAtRef.current) return 0;
    return Math.round((Date.now() - openedAtRef.current) / 1000);
  };

  const changeDialogStates = (
    setState: (value: SetStateAction<boolean>) => void,
    stateVal: boolean
  ) => {
    setState(stateVal);
    handleCloseActionsMenu();
  };

  const openDialog = (action: NEWS_ACTIONS) => {
    switch (action) {
      case NEWS_ACTIONS.EditNews:
        changeDialogStates(setIsEditDialogOpen, true);
        getSingleNews(setFetchedSingleNews);
        break;
      case NEWS_ACTIONS.Publish:
        changeDialogStates(setIsPublishDialogOpen, true);
        break;
      case NEWS_ACTIONS.Delete:
        changeDialogStates(setIsDeleteDialogOpen, true);
        break;
      default:
        return;
    }
  };

  const openSingleNewsDialog = () => {
    !isActionsOpen && setIsSingleNewsDialogOpen(true);
    getSingleNews(setFetchedSingleNews);

    newsOpenedAtRef.current = Date.now();

    pushDataLayerEvent(
      {
        ...userInfo,
        event: "news_open",
        news_item_id: newsData.id,
        action_date: moment.utc().format(),
        news_item_title: newsData.title,
        time_on_article: 0
      },
      cookieConsentStatus
    );
  };

  const openReleaseNotesHistoryDialog = () => {
    !isActionsOpen && setIsReleaseNotesDialogOpen(true);
    getSingleNews(setFetchedSingleNews);

    releaseNotesOpenedAtRef.current = Date.now();

    pushDataLayerEvent(
      {
        ...userInfo,
        event: "release_notes_open",
        news_item_id: newsData.id,
        action_date: moment.utc().format(),
        news_item_title: newsData.title,
        time_on_article: 0
      },
      cookieConsentStatus
    );
  };

  const handleCloseSingleNews = () => {
    pushDataLayerEvent(
      {
        ...userInfo,
        event: "news_close",
        news_item_id: newsData.id,
        action_date: moment.utc().format(),
        news_item_title: newsData.title,
        time_on_article: getElapsedSeconds(newsOpenedAtRef)
      },
      cookieConsentStatus
    );

    newsOpenedAtRef.current = null;
    setIsSingleNewsDialogOpen(false);
  };

  const handleCloseReleaseNotes = () => {
    setIsReleaseNotesDialogOpen(false);

    pushDataLayerEvent(
      {
        ...userInfo,
        event: "release_notes_close",
        news_item_id: newsData.id,
        action_date: moment.utc().format(),
        news_item_title: newsData.title,
        time_on_article: getElapsedSeconds(releaseNotesOpenedAtRef)
      },
      cookieConsentStatus
    );

    releaseNotesOpenedAtRef.current = null;
  };

  const handleCloseDialog = (action: NEWS_ACTIONS) => {
    switch (action) {
      case NEWS_ACTIONS.EditNews:
        changeDialogStates(setIsEditDialogOpen, false);
        break;
      case NEWS_ACTIONS.Publish:
        changeDialogStates(setIsPublishDialogOpen, false);
        break;
      case NEWS_ACTIONS.Delete:
        changeDialogStates(setIsDeleteDialogOpen, false);
        break;
      case NEWS_ACTIONS.ReleaseNotes:
        handleCloseReleaseNotes();
        break;
      default:
        handleCloseSingleNews();
    }
  };

  const handleOpenActionsMenu = (e: {
    currentTarget: SetStateAction<MenuAnchorEl>;
    stopPropagation: () => void;
  }) => {
    e.stopPropagation();
    setIsActionsOpen?.(e.currentTarget);
  };

  return (
    <>
      <Card
        data-testid="single-news-card"
        elevation={0}
        sx={[newsCard, noMargin && cardNoMargin] as CSSObject[]}
        onClick={
          isReleaseNotes ? openReleaseNotesHistoryDialog : openSingleNewsDialog
        }
      >
        <div css={css(textColumn)}>
          <div
            css={css({
              flex: 1,
              minWidth: 0,
              marginRight: "8px"
            })}
          >
            {unpublishedNew && (
              <DraftAdditions
                onOpenActionsMenu={handleOpenActionsMenu}
                onCloseActionsMenu={handleCloseActionsMenu}
                isActionsOpen={isActionsOpen}
                openDialog={openDialog}
              />
            )}

            <ListedNewsItemContent newsData={newsData} />
          </div>

          <div
            css={css({
              width: NEWS_SECTION.listImageWidth,
              minHeight: NEWS_SECTION.listImageHeight,
              flexShrink: 0
            })}
          >
            <NewsImageContainer
              image={releaseNotesImage || imageFromResponse || undefined}
              listItem
            />
          </div>
        </div>
      </Card>

      <SingleNewsDialog
        title={newsData.title}
        newsData={fetchedSingleNews}
        isOpen={isSingleNewsDialogOpen}
        onClose={() => handleCloseDialog(NEWS_ACTIONS.OpenNew)}
        isLoading={isLoading}
      />

      <EditNewsDialog
        isLoading={isLoading}
        newsData={fetchedSingleNews}
        isDialogOpen={isEditDialogOpen}
        onClose={() => handleCloseDialog(NEWS_ACTIONS.EditNews)}
      />
      <PublishNewsDialog
        isDialogOpen={isPublishDialogOpen}
        onClose={() => handleCloseDialog(NEWS_ACTIONS.Publish)}
        newsData={newsData}
        isReleaseNotes={isReleaseNotes}
      />

      <DeleteNewsDialog
        isDialogOpen={isDeleteDialogOpen}
        onClose={() => handleCloseDialog(NEWS_ACTIONS.Delete)}
        newsData={newsData}
        isReleaseNotes={isReleaseNotes}
      />

      <ReleaseNotesDialog
        isOpen={isReleaseNotesDialogOpen}
        onClose={() => handleCloseDialog(NEWS_ACTIONS.ReleaseNotes)}
        newsData={fetchedSingleNews}
        isLoading={isLoading}
      />
    </>
  );
};

export default SingleNews;
